import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Search, CheckCircle, Circle, PlayCircle } from 'lucide-react';
import { api, formatDate, downloadCsv } from '../../lib/api';

export const TIME_NOTE =
  'Estimated from activity: the time between a learner’s actions, counting any gap of more than 10 minutes as 10 minutes. It isn’t certified seat time.';

const STATUS = {
  not_started: { label: 'Not started', Icon: Circle, cls: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'In progress', Icon: PlayCircle, cls: 'bg-blue-50 text-blue-800' },
  completed: { label: 'Completed', Icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-800' },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.not_started;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`}>
      <s.Icon size={12} aria-hidden="true" /> {s.label}
    </span>
  );
}

export const fraction = (x) => (x && x.total ? `${x.done}/${x.total}` : '—');
export const minutes = (m) => (m == null ? '—' : m < 1 ? '<1 min' : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`);

function Bar({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 rounded-full bg-slate-200" aria-hidden="true">
        <div className="h-full rounded-full bg-slate-700" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-600">{fraction({ done, total })}</span>
    </div>
  );
}

function median(nums) {
  const s = nums.filter((n) => n != null).sort((a, b) => a - b);
  if (!s.length) return null;
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
}

// Per-learner progress for a Course Engine course. Rows open the learner's
// block-by-block detail.
export default function EngineProgressTab({ courseId, courseTitle }) {
  const [learners, setLearners] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api(`/api/admin/course-engine/courses/${courseId}/progress`)
      .then((d) => setLearners(d.learners))
      .catch((e) => setError(e.message));
  }, [courseId]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (learners || []).filter(
      (l) => (status === 'all' || l.status === status) && (!needle || `${l.name ?? ''} ${l.email}`.toLowerCase().includes(needle)),
    );
  }, [learners, q, status]);

  if (error) return <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</p>;
  if (!learners) return <p className="text-slate-600">Loading progress…</p>;

  const started = learners.filter((l) => l.status !== 'not_started').length;
  const hasQuizzes = learners.some((l) => l.totals?.quizzes?.total);
  const completed = learners.filter((l) => l.status === 'completed').length;
  const tiles = [
    { label: 'Enrolled', value: learners.length },
    { label: 'Started', value: started },
    { label: 'Completed', value: completed },
    { label: 'Median time (est.)', value: minutes(median(learners.filter((l) => l.status !== 'not_started').map((l) => l.totals?.active_minutes))) },
  ];

  const exportProgress = () =>
    downloadCsv(
      shown.map((l) => ({
        learner: l.name,
        email: l.email,
        status: STATUS[l.status]?.label ?? l.status,
        lessons_completed: l.totals?.lessons.done ?? '',
        lessons_total: l.totals?.lessons.total ?? '',
        activities_completed: l.totals?.activities.done ?? '',
        activities_total: l.totals?.activities.total ?? '',
        blocks_seen: l.totals?.seen.done ?? '',
        blocks_total: l.totals?.seen.total ?? '',
        quiz_answered: l.totals?.kc.answered ?? '',
        quiz_total: l.totals?.kc.total ?? '',
        quiz_correct_first_try: l.totals?.kc.first_try_correct ?? '',
        ...(hasQuizzes ? { graded_quizzes_passed: l.totals?.quizzes?.passed ?? '', graded_quizzes_total: l.totals?.quizzes?.total ?? '', graded_quiz_score: l.totals?.quizzes?.score ?? '' } : {}),
        est_minutes: l.totals?.active_minutes ?? '',
        first_activity: l.totals?.first_activity_at ?? '',
        last_activity: l.totals?.last_activity_at ?? l.last_interaction_at ?? '',
        enrolled: l.enrolled_at,
        completed: l.completed_at ?? '',
        version: l.version ?? '',
        certificate: l.certificate_number ?? '',
      })),
      `${courseTitle} - learner progress.csv`,
      [[`Course: ${courseTitle}`], [`Exported ${new Date().toLocaleString()}`], [TIME_NOTE]],
    );

  const exportAnswers = async () => {
    setBusy(true);
    try {
      const { answers } = await api(`/api/admin/course-engine/courses/${courseId}/progress/answers`);
      if (!answers.length) {
        alert('No quiz answers yet.');
        return;
      }
      downloadCsv(answers, `${courseTitle} - quiz answers.csv`, [[`Course: ${courseTitle}`], [`Exported ${new Date().toLocaleString()}`]]);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t.label}</p>
            <p className="mt-1 text-2xl font-bold">{t.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-semibold">Learner progress</h2>
            <span className="text-xs text-slate-600">
              {shown.length} of {learners.length}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <span className="sr-only">Search learners</span>
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" aria-hidden="true" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email" className="input pl-8 py-1.5 text-sm w-48" />
            </label>
            <label>
              <span className="sr-only">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="input py-1.5 text-sm">
                <option value="all">All statuses</option>
                <option value="not_started">Not started</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
              </select>
            </label>
            <button type="button" onClick={exportProgress} disabled={!shown.length} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-40">
              <Download size={14} /> Progress CSV
            </button>
            <button type="button" onClick={exportAnswers} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-40">
              <Download size={14} /> Quiz answers CSV
            </button>
          </div>
        </div>

        {shown.length === 0 ? (
          <p className="p-5 text-sm text-slate-600">{learners.length ? 'No learners match.' : 'No one is enrolled yet.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-600 bg-slate-50">
                <tr>
                  <th className="px-4 py-2">Learner</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Lessons</th>
                  <th className="px-4 py-2">Activities</th>
                  <th className="px-4 py-2">Blocks seen</th>
                  <th className="px-4 py-2">Knowledge checks</th>
                  {hasQuizzes ? <th className="px-4 py-2">Graded quiz</th> : null}
                  <th className="px-4 py-2">
                    Time <span className="normal-case font-normal">(est.)</span>
                  </th>
                  <th className="px-4 py-2">Last active</th>
                  <th className="px-4 py-2">Version</th>
                  <th className="px-4 py-2">Certificate</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((l) => (
                  <tr key={l.enrollment_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/admin/courses/${courseId}/learners/${l.enrollment_id}`} className="font-medium text-slate-900 hover:underline">
                        {l.name || l.email}
                      </Link>
                      <span className="block text-xs text-slate-600">{l.email}</span>
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={l.status} />
                    </td>
                    <td className="px-4 py-2">{l.totals ? <Bar {...l.totals.lessons} /> : '—'}</td>
                    <td className="px-4 py-2 text-slate-700">{fraction(l.totals?.activities)}</td>
                    <td className="px-4 py-2 text-slate-700">{fraction(l.totals?.seen)}</td>
                    <td className="px-4 py-2 text-slate-700">
                      {l.totals?.kc.total ? (
                        <>
                          {l.totals.kc.answered}/{l.totals.kc.total} answered
                          <span className="block text-xs text-slate-600">{l.totals.kc.first_try_correct} right first try</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    {hasQuizzes ? (
                      <td className="px-4 py-2 text-slate-700">
                        {l.totals?.quizzes?.total ? (
                          <>
                            {l.totals.quizzes.passed}/{l.totals.quizzes.total} passed
                            {l.totals.quizzes.score != null ? <span className="block text-xs text-slate-600">score {l.totals.quizzes.score}%</span> : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    ) : null}
                    <td className="px-4 py-2 text-slate-700">{l.status === 'not_started' ? '—' : minutes(l.totals?.active_minutes)}</td>
                    <td className="px-4 py-2 text-slate-700">{formatDate(l.totals?.last_activity_at ?? l.last_interaction_at) || '—'}</td>
                    <td className="px-4 py-2 text-slate-700">{l.version ? `v${l.version}` : '—'}</td>
                    <td className="px-4 py-2 text-slate-700">{l.certificate_number || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="px-5 py-3 border-t border-slate-100 text-xs text-slate-600">
          {TIME_NOTE} A lesson counts as complete when the learner reaches its end with every Continue passed; activities aren’t required unless a Continue is set to wait for them. A course with graded quizzes also needs every one passed, and its certificate score is their average.
        </p>
      </div>
    </div>
  );
}
